import type { MiraklListingIdentity, MiraklProductId } from '@noths/polaris-client-mirakl-tracking';
import { MIRAKL_PAGE } from '@noths/polaris-client-mirakl-tracking';
import { getProductIdFromProductCode } from '@noths/polaris-client-utils';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectCurrentPage } from 'src/redux/products/selectors/selectors';
import { selectPageCategories } from 'src/redux/productsQuery/selectors/selectPageCategories';
import { getCategoryIdFromCanonicalFilters } from 'src/redux/productsQuery/utils/getCategoryIdFromCanonicalFilters';
import { PageType } from 'src/types/navigation';

/** Mirakl wants the first 20 organic products of a page. */
export const MIRAKL_VIEW_PRODUCT_LIMIT = 20;

export type MiraklListingViewEvent = MiraklListingIdentity & {
  pageNumber: number;
  productIds: MiraklProductId[];
};

const getListingIdentity = (state: ReduxApplicationState): MiraklListingIdentity | null => {
  const { navigation, productsQuery } = state;

  switch (navigation.pageType) {
    case PageType.Search:
      return productsQuery.searchTerm
        ? { keywords: productsQuery.searchTerm, pageId: MIRAKL_PAGE.SEARCH }
        : null;
    case PageType.Category: {
      const categoryId = getCategoryIdFromCanonicalFilters(productsQuery.canonicalFilters);

      return categoryId === null
        ? null
        : { categoryId: String(categoryId), pageId: MIRAKL_PAGE.PLP };
    }
    case PageType.Taxonomy: {
      const categoryId = selectPageCategories(state)?.[0];

      return categoryId ? { categoryId, pageId: MIRAKL_PAGE.PLP } : null;
    }
    default:
      return null;
  }
};

/**
 * The Mirakl `view` event for the listing page in the store, or null when the page has no identity
 * Mirakl knows (partner pages) or the listings request failed. A page with no results still sends
 * the view with an empty product list: Mirakl asked for the search view with and without results.
 * `categoryId` is the legacy category id on a category page and the product-type breadcrumb on a
 * taxonomy page, which is what the ad requests will send too.
 */
export const getMiraklViewEvent = (state: ReduxApplicationState): MiraklListingViewEvent | null => {
  const identity = getListingIdentity(state);
  const pageNumber = selectCurrentPage(state);

  if (!identity || state.products.error || !pageNumber) {
    return null;
  }

  const products = state.products.pages[pageNumber] ?? [];

  return {
    ...identity,
    pageNumber,
    productIds: products
      .slice(0, MIRAKL_VIEW_PRODUCT_LIMIT)
      .map(({ code }) => getProductIdFromProductCode(code)),
  };
};
